import React, { useEffect, useState } from 'react';
import { Eye, Heart, ShoppingCart, TrendingUp, ArrowLeft, Award } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../services/firebase';
import { motion } from 'framer-motion';

const Analytics: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalViews: 0,
    totalLikes: 0,
    totalArtworks: 0,
    totalFollowers: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      loadAnalytics();
    }
  }, [user]);

  const loadAnalytics = async () => {
    if (!user) return;

    try {
      // Get user's artworks
      const artworksQuery = query(
        collection(db, 'artworks'),
        where('artistId', '==', user.id)
      );
      const artworksSnapshot = await getDocs(artworksQuery);
      
      let totalViews = 0;
      let totalLikes = 0;
      
      artworksSnapshot.docs.forEach(doc => {
        const data = doc.data();
        totalViews += data.views || 0;
        totalLikes += data.likes || 0;
      });

      // Get followers count
      const followersQuery = query(
        collection(db, 'follows'),
        where('followingId', '==', user.id)
      );
      const followersSnapshot = await getDocs(followersQuery);

      setStats({
        totalViews,
        totalLikes,
        totalArtworks: artworksSnapshot.size,
        totalFollowers: followersSnapshot.size,
      });
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { icon: Eye, label: 'Total Views', value: stats.totalViews, color: 'from-blue-500 to-blue-600', textColor: 'text-blue-600' },
    { icon: Heart, label: 'Total Likes', value: stats.totalLikes, color: 'from-red-500 to-pink-600', textColor: 'text-red-600' },
    { icon: ShoppingCart, label: 'Artworks', value: stats.totalArtworks, color: 'from-green-500 to-emerald-600', textColor: 'text-green-600' },
    { icon: TrendingUp, label: 'Followers', value: stats.totalFollowers, color: 'from-purple-500 to-purple-600', textColor: 'text-purple-600' },
  ];

  const engagementRate = stats.totalViews > 0 ? ((stats.totalLikes / stats.totalViews) * 100).toFixed(1) : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-pink-50 pb-24">
      {/* Header */}
      <div className="bg-white shadow-sm p-4 flex items-center gap-4 sticky top-0 z-10">
        <motion.button
          whileTap={{ scale: 0.95 }}
          onClick={() => navigate('/profile')}
          className="p-2 hover:bg-gray-100 rounded-full"
        >
          <ArrowLeft size={24} />
        </motion.button>
        <h1 className="text-xl font-bold text-gray-800">Your Analytics</h1>
      </div>

      <div className="p-4">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-4 mb-8">
          {statCards.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all"
            >
              <div className={`w-12 h-12 bg-gradient-to-br ${stat.color} rounded-xl flex items-center justify-center mb-3`}>
                <stat.icon className="text-white" size={24} />
              </div>
              <p className="text-3xl font-bold text-gray-800 mb-1">{stat.value}</p>
              <p className="text-sm text-gray-600">{stat.label}</p>
            </motion.div>
          ))}
        </div>

        {/* Engagement Rate */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-2xl p-6 shadow-lg mb-6"
        >
          <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
            <TrendingUp className="text-primary" size={24} />
            Performance Metrics
          </h3>
          
          <div className="mb-4">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-gray-600">Engagement Rate</span>
              <span className="font-bold text-primary">{engagementRate}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${engagementRate}%` }}
                transition={{ duration: 1, delay: 0.5 }}
                className="bg-gradient-to-r from-primary to-purple-600 h-full rounded-full"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4 mt-6">
            <div className="text-center p-3 bg-blue-50 rounded-lg">
              <p className="text-2xl font-bold text-blue-600">{(stats.totalViews / (stats.totalArtworks || 1)).toFixed(0)}</p>
              <p className="text-xs text-gray-600">Avg Views/Post</p>
            </div>
            <div className="text-center p-3 bg-red-50 rounded-lg">
              <p className="text-2xl font-bold text-red-600">{(stats.totalLikes / (stats.totalArtworks || 1)).toFixed(0)}</p>
              <p className="text-xs text-gray-600">Avg Likes/Post</p>
            </div>
            <div className="text-center p-3 bg-purple-50 rounded-lg">
              <p className="text-2xl font-bold text-purple-600">{stats.totalFollowers}</p>
              <p className="text-xs text-gray-600">Followers</p>
            </div>
          </div>
        </motion.div>

        {/* Premium Upgrade */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-gradient-to-r from-primary via-purple-600 to-pink-500 rounded-2xl p-6 text-white shadow-xl"
        >
          <div className="flex items-center gap-3 mb-4">
            <Award size={32} />
            <h3 className="text-2xl font-bold">Upgrade to Premium</h3>
          </div>
          <p className="opacity-90 mb-6">
            Get detailed insights, advanced analytics, and priority support
          </p>
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate('/promotion')}
            className="w-full px-6 py-3 bg-white text-primary rounded-xl font-bold hover:shadow-lg transition-all"
          >
            View Plans
          </motion.button>
        </motion.div>
      </div>
    </div>
  );
};

export default Analytics;